import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { VehicleDayReport } from '../types';

/** Minuti kao „6 h 22" — tako se čita brže nego kao 382. */
function trajanje(minuta: number): string {
  const znak = minuta < 0 ? '−' : '';
  const m = Math.abs(Math.round(minuta));
  const h = Math.floor(m / 60);

  return h > 0 ? `${znak}${h} h ${String(m % 60).padStart(2, '0')}` : `${znak}${m} min`;
}

/**
 * Koliko dan vozila traje, i koliko od toga tura uopšte pokriva.
 *
 * ZAŠTO OVAJ BLOK POSTOJI
 *   Vlasnik je 29.09.2026. rekao: „ture ne prelaze radno vreme ni izbliza, a
 *   teško se stiže". Merenje je pokazalo zašto — vozač dugmad „Krenuo sam" i
 *   „Završi turu" pritiska NA STANICAMA, pa zabeležena tura ne pokriva utovar,
 *   vožnju do prve stanice, povratak ni istovar.
 *
 *   Medijana zabeležene ture je bila 3 h 33, a vozilo je van kruga magacina
 *   6 h 22. Dva i po sata po turi nisu postojala ni u jednom broju.
 *
 * Kraj istovara se NE prikazuje jer se ne meri: nijedan zapis ne kaže kad je
 * vozilo ispražnjeno.
 */
export default function VehicleDay({ days }: { days: number }) {
  const [report, setReport] = useState<VehicleDayReport | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await api.vehicleDay(days));
      setGreska(null);
    } catch (e) {
      setReport(null);
      setGreska(e instanceof Error ? e.message : String(e));
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  if (greska) {
    return (
      <section className="panel">
        <h2>Dan vozila</h2>
        <div className="health health--bad">Nije se moglo izračunati: {greska}</div>
      </section>
    );
  }

  if (!report) {
    return (
      <section className="panel">
        <h2>Dan vozila</h2>
        <p className="muted">Učitavam…</p>
      </section>
    );
  }

  const { vehicleDayMedian, recordedMedian, beforeTourMedian, afterTourMedian } = report;

  return (
    <section className="panel">
      <h2>Dan vozila</h2>

      <p className="note">
        Koliko je vozilo stvarno van magacina, naspram onoga što je zabeleženo kao
        tura. Polazak i povratak se mere po GPS-u; vozačev potez ima prednost kad
        je napravljen u dvorištu.
      </p>

      {vehicleDayMedian === null ? (
        <p className="muted">
          U poslednjih {days} dana nema dovoljno tura sa izmerenim polaskom i
          povratkom — presek se ne prikazuje.
        </p>
      ) : (
        <>
          <p className="brzina__broj">
            <strong>{trajanje(vehicleDayMedian)}</strong>
            <span className="muted"> · vozilo van magacina, medijana ({report.tours} tura)</span>
          </p>

          {recordedMedian !== null && (
            <p>
              Zabeleženo kao tura: <strong>{trajanje(recordedMedian)}</strong>.
              {vehicleDayMedian > recordedMedian && (
                <> Razlika je <strong>{trajanje(vehicleDayMedian - recordedMedian)}</strong> —
                utovar, vožnja do prve stanice, povratak i istovar.</>
              )}
            </p>
          )}

          <ul className="rows">
            {beforeTourMedian !== null && (
              <li>
                <span className="row__name">Pre nego što tura počne</span>
                <span className="row__meta">
                  od izlaska vozila iz kruga do „Krenuo sam" na telefonu
                </span>
                <span className="row__side">{trajanje(beforeTourMedian)}</span>
              </li>
            )}

            {afterTourMedian !== null && (
              <li>
                <span className="row__name">Posle „Završi turu"</span>
                <span className="row__meta">
                  {afterTourMedian < 0
                    ? 'vozač zatvara turu PRE nego što se vrati u magacin — za toliko'
                    : 'od zatvaranja ture do povratka u krug magacina'}
                </span>
                <span className="row__side">{trajanje(Math.abs(afterTourMedian))}</span>
              </li>
            )}
          </ul>

          <p className="note">
            <strong>Istovar se ne meri.</strong> Nijedan zapis ne kaže kad je vozilo
            ispražnjeno, pa se taj broj ne prikazuje — umesto procene stoji ono što
            je izmereno: kad se vozilo vratilo.
          </p>
        </>
      )}

      {report.withoutWindow > 0 && (
        <p className="note">
          Za {report.withoutWindow}{' '}
          {report.withoutWindow === 1 ? 'turu' : 'tura'} polazak i povratak nisu
          izmereni — vozilo tog dana nema GPS zapisa.
        </p>
      )}
    </section>
  );
}
